import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useGetPurchaseList } from "../../client/hooks/purchases/purchase.queries";
import PurchaseOrderDialog from "./Components/NewPurchaseOrder.component";
import PurchaseOrderDetail from "./Components/PurchaseOrderDetail.component";
import PurchaseOrderHeader from "./Components/PurchaseOrderHeader.component";
import PurchaseOrderStats from "./Components/PurchaseOrderStats.component";
import PurchaseOrderFilters from "./Components/PurchaseOrderFilters.component";
import PurchaseOrderTable from "./Components/PurchaseOrderTable.component";
import CustomSnackbar from "../components/Message.Component";
import AccessGuard from "../../components/AccessControl/AccessGuard";

const getStatusFromSituacao = (situacao) => {
  const statusMap = {
    0: "Em aberto",
    1: "Atendido",
    2: "Cancelado",
    3: "Em andamento",
  };
  return statusMap[situacao] || "Em aberto";
};

const getSituacaoFromStatus = (status) => {
  const situacaoMap = {
    "Em aberto": 0,
    Atendido: 1,
    Cancelado: 2,
    "Em andamento": 3,
  };
  return situacaoMap[status];
};

export default function PurchaseOrder() {
  const tableContainerRef = useRef(null);
  const [page, setPage] = useState({ limit: 20, page: 0 });

  // Estados temporários (campos do formulário)
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [createdAtInicio, setCreatedAtInicio] = useState(null);
  const [createdAtFim, setCreatedAtFim] = useState(null);

  // Estados aplicados (usados na requisição)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedCreatedAtInicio, setAppliedCreatedAtInicio] = useState(null);
  const [appliedCreatedAtFim, setAppliedCreatedAtFim] = useState(null);
  const [appliedSituacao, setAppliedSituacao] = useState("");

  // Parâmetros para a API
  const purchaseParams = {
    page: page.page,
    limit: page.limit,
    ...(appliedSearchTerm && { search: appliedSearchTerm }),
    ...(appliedCreatedAtInicio && {
      createdAtInicio: appliedCreatedAtInicio.format("YYYY-MM-DD"),
    }),
    ...(appliedCreatedAtFim && {
      createdAtFim: appliedCreatedAtFim.format("YYYY-MM-DD"),
    }),
    ...(appliedSituacao !== "" && { situacao: appliedSituacao }),
  };

  const {
    data: purchaseData,
    isError,
    isLoading,
  } = useGetPurchaseList(purchaseParams, true);

  const [alert, setAlert] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [order, setOrder] = useState({
    name: "",
    cnpj: "",
    email: "",
    originState: "",
    anymarketToken: "",
  });

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setAppliedCreatedAtInicio(createdAtInicio);
    setAppliedCreatedAtFim(createdAtFim);

    // Converte o status para o valor numérico da situação
    if (filterStatus !== "todos") {
      setAppliedSituacao(getSituacaoFromStatus(filterStatus));
    } else {
      setAppliedSituacao("");
    }

    setPage({ limit: page.limit, page: 0 });
  };

  const handleClearFilters = () => {
    // Limpa filtros temporários
    setSearchTerm("");
    setFilterStatus("todos");
    setCreatedAtInicio(null);
    setCreatedAtFim(null);

    // Limpa filtros aplicados
    setAppliedSearchTerm("");
    setAppliedCreatedAtInicio(null);
    setAppliedCreatedAtFim(null);
    setAppliedSituacao("");
  };

  // Debounce para busca automática após 2 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== appliedSearchTerm) {
        setAppliedSearchTerm(searchTerm);
        setPage({ limit: page.limit, page: 0 });
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchTerm, appliedSearchTerm, page.limit]);

  useEffect(() => {
    if (purchaseData?.data) {
      const formattedData = purchaseData.data.map((item) => ({
        id: item._id,
        numero: item.data?.numero || "N/A",
        contact_name:
          item.data?.fornecedor?.fantasia ||
          item.data?.fornecedor?.nome ||
          "Fornecedor não informado",
        date: item.data?.data || new Date().toLocaleDateString("pt-BR"),
        email: item.data?.fornecedor?.email || "Email não informado",
        note:
          item.data?.observacoes ||
          item.data?.observacoesInternas ||
          "Sem observações",
        quantity:
          item.data?.itens?.reduce((acc, item) => acc + item.quantidade, 0) ||
          0,
        unity_value: item.data?.total || 0,
        status: getStatusFromSituacao(item.data?.situacao?.valor),
        supplierLogo: "https://via.placeholder.com/40",
        imported: item.imported,
        imported_channel: item.imported_channel,
        marketplace_id: item.marketplace_id,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        originalData: item.data,
      }));
      setRows(formattedData);
    }
  }, [purchaseData, isLoading]);

  useEffect(() => {
    // Atualiza automaticamente quando o filterStatus mudar (chips de status)
    if (filterStatus !== "todos") {
      setAppliedSituacao(getSituacaoFromStatus(filterStatus));
    } else {
      setAppliedSituacao("");
    }
  }, [filterStatus]);

  const openOrderDialog = (ordem = null) => {
    if (ordem !== null) {
      setOrder(ordem);
    } else {
      setOrder({});
    }
    setOpenDialog(true);
  };

  const openDetailModal = (purchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setOpenDetailDialog(true);
  };

  const closeDetailModal = () => {
    setOpenDetailDialog(false);
    setSelectedPurchaseOrder(null);
  };

  const loadMsg = () => {
    setOpenDialog(false);
    setAlert(true);
    setSuccessMsg("Ordem criada com sucesso");
  };

  const closeDialog = (reload) => {
    setOpenDialog(false);
    if (reload) {
      setTimeout(() => setOrder({}), 500);
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage({ page: newPage, limit: page.limit });
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setPage({ limit: parseInt(event.target.value, 10), page: 0 });
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  const filteredRows =
    searchTerm || filterStatus !== "todos"
      ? rows.filter((row) => {
          const matchesSearch =
            !searchTerm ||
            row.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(row.numero).includes(searchTerm);

          const matchesStatus =
            filterStatus === "todos" || row.status === filterStatus;

          return matchesSearch && matchesStatus;
        })
      : rows;

  const uniqueStatuses = ["todos", ...new Set(rows.map((row) => row.status))];
  const totalValue = rows.reduce(
    (total, row) => total + parseFloat(row.unity_value || 0),
    0,
  );

  return (
    <AccessGuard feature="purchase-orders">
      <CustomSnackbar
        open={alert}
        onClose={() => setAlert(false)}
        severity={isError ? "error" : "success"}
        message={successMsg}
      />

      <PurchaseOrderHeader onNewOrder={() => openOrderDialog()} />

      <Box sx={{ p: 3 }}>
        <PurchaseOrderStats totalOrders={rows.length} totalValue={totalValue} />
      </Box>

      <PurchaseOrderFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterStatus={filterStatus}
        onFilterChange={setFilterStatus}
        uniqueStatuses={uniqueStatuses}
        createdAtInicio={createdAtInicio}
        setCreatedAtInicio={setCreatedAtInicio}
        createdAtFim={createdAtFim}
        setCreatedAtFim={setCreatedAtFim}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
      />

      <PurchaseOrderTable
        filteredRows={
          searchTerm || filterStatus !== "todos" ? filteredRows : rows
        }
        isLoading={isLoading}
        onRowClick={openDetailModal}
        onNewOrder={() => openOrderDialog()}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        totalCount={
          searchTerm || filterStatus !== "todos"
            ? filteredRows.length
            : purchaseData?.total || rows.length
        }
        tableContainerRef={tableContainerRef}
      />

      <PurchaseOrderDialog
        open={openDialog}
        onClose={closeDialog}
        onSave={loadMsg}
        initialData={order}
        orderNumber={rows.length + 1}
      />

      <PurchaseOrderDetail
        open={openDetailDialog}
        onClose={closeDetailModal}
        purchaseOrder={selectedPurchaseOrder}
      />
    </AccessGuard>
  );
}
